import React, { useState, useEffect } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import apiClient from "../../utils/apiClient";
import { AppButton } from "../ui";

const TransferStudentsModal = ({
  show,
  onHide,
  selectedIds = [],
  onTransfer,
  mode = "students", // "students" or "batches"
}) => {
  const [courseOptions, setCourseOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [form, setForm] = useState({ course: "", batch: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetch all courses
  useEffect(() => {
    if (show) {
      apiClient.get("/courses/list").then(({ data }) => {
        setCourseOptions((data.courses || []).map((c) => ({
          value: c._id,
          label: c.name,
        })));
      });
      setForm({ course: "", batch: "" });
      setBatchOptions([]);
      setError("");
    }
  }, [show]);

  // Fetch batches for selected course
  useEffect(() => {
    if (form.course) {
      apiClient
        .get("/batches/list", { params: { course: form.course } })
        .then(({ data }) =>
          setBatchOptions(
            (data.batches || []).map((b) => ({
              value: b._id,
              label: b.name,
            }))
          )
        );
    } else {
      setBatchOptions([]);
    }
  }, [form.course]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "course" ? { batch: "" } : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      // API for students: /students/transfer (POST: {ids, course, batch})
      // API for batches: /batches/transfer (POST: {ids, course, batch})
      const path = mode === "batches" ? "/batches/transfer" : "/students/transfer";
      await apiClient.put(path, {
        ids: selectedIds,
        course: form.course,
        batch: form.batch,
      });
      if (onTransfer) onTransfer();
      onHide();
    } catch (err) {
      setError(err?.response?.data?.message || "Transfer failed. Please try again.");
    }
    setLoading(false);
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Transfer {mode === "students" ? "Students" : "Batches"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <div className="alert alert-danger py-2 small mb-3">{error}</div>}
          <div className="mb-3">
            <Form.Label>New Course</Form.Label>
            <Form.Select
              name="course"
              value={form.course}
              onChange={handleChange}
              required
            >
              <option value="">Select course</option>
              {courseOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Form.Select>
          </div>
          <div>
            <Form.Label>New Batch</Form.Label>
            <Form.Select
              name="batch"
              value={form.batch}
              onChange={handleChange}
              required
              disabled={!form.course}
            >
              <option value="">Select batch</option>
              {batchOptions.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </Form.Select>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <AppButton variant="outline" onClick={onHide} disabled={loading}>
            Cancel
          </AppButton>
          <AppButton
            type="submit"
            disabled={!form.course || !form.batch || loading}
          >
            {loading ? <Spinner size="sm" /> : "Transfer"}
          </AppButton>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default TransferStudentsModal;
